import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createVNode as _createVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref, vShow as _vShow, withDirectives as _withDirectives, resolveComponent as _resolveComponent } from "vue"

const _hoisted_1 = { class: "book-exercise" }
const _hoisted_2 = { class: "book-exercise__header" }
const _hoisted_3 = { class: "book-exercise__banner" }
const _hoisted_4 = { class: "book-exercise__title" }
const _hoisted_5 = ["innerHTML"]
const _hoisted_6 = { class: "book-exercise__body" }
const _hoisted_7 = { class: "book-exercise__label-wrapper" }
const _hoisted_8 = { class: "book-exercise__question-number-wrapper" }
const _hoisted_9 = { class: "book-exercise__question-number" }
const _hoisted_10 = ["for", "innerHTML"]
const _hoisted_11 = {
  key: 0,
  class: "book-exercise__controls"
}
const _hoisted_12 = { class: "book-exercise__buttons" }
const _hoisted_13 = { class: "book-exercise__error" }
const _hoisted_14 = {
  key: 1,
  class: "book-exercise__threads"
}
const _hoisted_15 = { class: "book-exercise__thread-title" }
const _hoisted_16 = { class: "book-exercise__reply-count" }
const _hoisted_17 = { class: "book-exercise__thread-date" }

import { ref, Ref, computed, watch } from 'vue'
import { useRouter } from 'vue-router'
import { Sentry } from '@/services/sentry'

import { OpenQuestionsView } from '@/models/doc.book'
import { convertError } from '@/models/error'
import { formatTrialDate } from '@/helpers/time'
import { useWait } from '@/app/composables/useWait'
import { assertNotNull } from '@/helpers/typing'

import InputWithContextMenu from '@/app/components/InputWithContextMenu.vue'
import AppButton from '@/app/components/button/AppButton.vue'
import EOCButton from '@/app/components/billing/EOCButton.vue'


export default /*@__PURE__*/_defineComponent({
  __name: 'Exercise',
  props: {
    content: {},
    isEOC: { type: Boolean }
  },
  setup(__props: any) {

const props = __props

const wait = useWait()
const router = useRouter()

const timeoutId: Ref<number | undefined> = ref(undefined)
const isSaving = ref(false)
const editSubmitted = ref(false)
const errorRef: Ref<string | null> = ref(null)

// For tests, to permanently disable timers,
// can be set by `disableTimers()` method call.
const timersDisabled = ref(false)

const exercise = computed(() => {
  return assertNotNull(props.content).currentExercise
})

// When moving from an exercise to another, this component
// isn't unmounted, so we have to manually reset the state
// when that happens.
watch(exercise, (previousExercise, newExercise) => {
  if (previousExercise.id !== newExercise.id) {
    timeoutId.value = undefined
    isSaving.value = false
    editSubmitted.value = false
    errorRef.value = null

    wait.end('Submitting')
    wait.end('Autosaving')
    wait.end('Sending')
  }
})

const exerciseTitle = computed(() => {
  return exercise.value.title.replace('Exercise: ', '')
})

const answers = computed(() => {
  if (props.isEOC) {
    return exercise.value.answers.slice(0, 1)
  }

  return exercise.value.answers
})

const readOnlyMode = computed(() => {
  return props.isEOC || (!!exercise.value.submitted_at && !editSubmitted.value)
})

const validationState = computed(() => {
  if (errorRef.value) {
    return false
  }

  return null
})

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function disableTimers(): void {
  timersDisabled.value = true
}

function switchToEdit(): void {
  editSubmitted.value = true
}

function formatTime(time: string): string {
  return formatTrialDate(time)
}

/**
 * Auto-saves exercise data as user enters is.
 */
async function onChange(): Promise<void> {
  errorRef.value = null
  // If a timer was already started, clear it.
  if (timeoutId.value || isSaving.value) {
    clearTimeout(timeoutId.value)
  }

  if (timersDisabled.value) {
    return
  }

  // Set timer that will save comment when it fires.
  timeoutId.value = setTimeout(async () => {
    // Make ajax call to save data.
    isSaving.value = true
    // TODO: Add UI for autosaving
    wait.start('Autosaving')
    try {
      await exercise.value.save()
    } finally {
      wait.end('Autosaving')
      isSaving.value = false
    }
  }, 750)
}

async function submit(): Promise<void> {
  isSaving.value = true
  errorRef.value = null
  try {
    wait.start('Submitting')
    await exercise.value.submit()
  } catch (error: any) {
    const err = convertError(error)
    if (err.isValidationError()) {
      errorRef.value = err.getOne('answers')
    } else {
      Sentry.captureException(error)
      throw error
    }
  } finally {
    wait.end('Submitting')
    isSaving.value = false
  }
  editSubmitted.value = false
}

async function discuss(): Promise<void> {
  errorRef.value = null

  try {
    wait.start('Sending')
    const thread: any = await exercise.value.discuss()
    router.push({
      name: 'book.edit_thread',
      state: {
        threadTitle: thread.title,
        threadText: thread.text,
        userWorkoutId: thread.user_workout_id,
      },
    })
  } finally {
    wait.end('Sending')
  }
}

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _cache[4] || (_cache[4] = _createElementVNode("div", { class: "book-exercise__tag" }, [
        _createElementVNode("div", { class: "iconfont ipen book-exercise__tag-icon" }),
        _createElementVNode("p", { class: "book-exercise__tag-text" }, "Exercise")
      ], -1)),
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("h2", _hoisted_4, _toDisplayString(exerciseTitle.value), 1),
        _createElementVNode("div", {
          class: "book-exercise__description",
          oncopy: "return false",
          innerHTML: exercise.value.text
        }, null, 8, _hoisted_5)
      ])
    ]),
    _createElementVNode("div", _hoisted_6, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(answers.value, (answer, index) => {
        return (_openBlock(), _createElementBlock("div", {
          key: answer.questionId,
          class: "book-exercise__question"
        }, [
          _createElementVNode("div", _hoisted_7, [
            _createElementVNode("div", _hoisted_8, [
              _createElementVNode("div", _hoisted_9, _toDisplayString(index + 1), 1)
            ]),
            _createElementVNode("label", {
              class: "book-exercise__label",
              for: answer.questionId,
              oncopy: "return false",
              innerHTML: answer.questionText
            }, null, 8, _hoisted_10)
          ]),
          _createVNode(InputWithContextMenu, {
            id: answer.questionId,
            modelValue: answer.answer,
            "onUpdate:modelValue": ($event: any) => ((answer.answer) = $event),
            elementType: "textarea",
            class: "book-exercise__input",
            disabled: readOnlyMode.value,
            placeholder: "Type your answer here.",
            rows: "1",
            autoResize: true,
            onInput: _cache[0] || (_cache[0] = ($event: any) => (onChange()))
          }, null, 8, ["id", "modelValue", "onUpdate:modelValue", "disabled"])
        ]))
      }), 128))
    ]),
    (!__props.isEOC)
      ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
          _createElementVNode("div", _hoisted_12, [
            (readOnlyMode.value)
              ? (_openBlock(), _createBlock(AppButton, {
                  key: 0,
                  class: "book-exercise__btn book-exercise__btn--edit",
                  icon: "ipen",
                  onClick: _cache[1] || (_cache[1] = ($event: any) => (switchToEdit()))
                }, {
                  default: _withCtx(() => [...(_cache[5] || (_cache[5] = [
                    _createTextVNode(" Edit ", -1)
                  ]))]),
                  _: 1
                }))
              : (_openBlock(), _createBlock(AppButton, {
                  key: 1,
                  class: "book-exercise__btn book-exercise__btn--submit",
                  loading: _unref(wait).is('Submitting'),
                  disabled: _unref(wait).is('Submitting'),
                  onClick: _cache[2] || (_cache[2] = ($event: any) => (submit()))
                }, {
                  default: _withCtx(() => [...(_cache[6] || (_cache[6] = [
                    _createTextVNode(" Save Answers ", -1)
                  ]))]),
                  _: 1
                }, 8, ["loading", "disabled"])),
            (exercise.value.submitted_at)
              ? (_openBlock(), _createBlock(AppButton, {
                  key: 2,
                  class: "book-exercise__btn book-exercise__btn--discuss",
                  icon: "icategory-communication",
                  outlined: true,
                  loading: _unref(wait).is('Sending'),
                  disabled: _unref(wait).is('Sending'),
                  onClick: _cache[3] || (_cache[3] = ($event: any) => (discuss()))
                }, {
                  default: _withCtx(() => [...(_cache[7] || (_cache[7] = [
                    _createTextVNode(" Discuss ", -1)
                  ]))]),
                  _: 1
                }, 8, ["loading", "disabled"]))
              : _createCommentVNode("", true)
          ]),
          _withDirectives(_createElementVNode("div", _hoisted_13, [
            _cache[8] || (_cache[8] = _createElementVNode("strong", null, "Error:", -1)),
            _createTextVNode(" " + _toDisplayString(errorRef.value), 1)
          ], 512), [
            [_vShow, validationState.value === false]
          ])
        ]))
      : _createCommentVNode("", true),
    (!__props.isEOC && exercise.value.threads && exercise.value.threads.length > 0)
      ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
          _cache[9] || (_cache[9] = _createElementVNode("h3", { class: "book-exercise__threads-title" }, " Discussions You Posted for This Exercise ", -1)),
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(exercise.value.threads, (thread) => {
            return (_openBlock(), _createBlock(_component_router_link, {
              key: thread.id,
              to: {
          name: __props.content.book.routeNames().thread,
          params: { thread: thread.url_slug },
        },
              class: "book-exercise__thread"
            }, {
              default: _withCtx(() => [
                _createElementVNode("p", _hoisted_15, [
                  _createTextVNode(_toDisplayString(thread.title) + " ", 1),
                  _createElementVNode("span", _hoisted_16, _toDisplayString(`- ${thread.comments_count} replies`), 1)
                ]),
                _createElementVNode("p", _hoisted_17, _toDisplayString(formatTime(thread.created)), 1)
              ]),
              _: 2
            }, 1032, ["to"]))
          }), 128))
        ]))
      : _createCommentVNode("", true),
    (__props.isEOC)
      ? (_openBlock(), _createBlock(EOCButton, {
          key: 2,
          text: "Subscribe to See Full Exercise ",
          "ios-text": "Full Subscription Required to Access Exercise"
        }))
      : _createCommentVNode("", true)
  ]))
}
}

})